import type { AuthSession, TenantModuleKey, TenantStaffRole } from '../types';

export const STAFF_ROLES: TenantStaffRole[] = ['owner', 'manager', 'content_editor'];

export const TENANT_MODULES: TenantModuleKey[] = [
  'overview',
  'announcements',
  'notes',
  'marketplace',
  'publisher',
  'export',
  'albums',
  'visiting_card',
  'stock_alerts',
  'crm',
  'orders',
  'products',
  'galleries',
  'album_reviews',
  'album_published_reviews',
  'album_requests',
  'album_books',
  'categories',
  'attributes',
  'optionSets',
  'variant_templates',
  'addon_templates',
  'subcategories',
  'import',
  'import_media',
  'prices',
  'promotions',
  'stock',
  'catalog',
  'settings',
  'staff',
  'profile',
];

const ALL_ACCESS = Object.fromEntries(TENANT_MODULES.map((key) => [key, true])) as Record<
  TenantModuleKey,
  boolean
>;

const ROLE_MODULES: Record<TenantStaffRole, Record<TenantModuleKey, boolean>> = {
  owner: ALL_ACCESS,
  manager: {
    overview: true,
    announcements: true,
    notes: true,
    marketplace: true,
    publisher: true,
    export: true,
    albums: true,
    visiting_card: true,
    stock_alerts: true,
    crm: true,
    orders: true,
    stock: true,
    catalog: true,
    products: false,
    galleries: false,
    album_reviews: true,
    album_published_reviews: true,
    album_requests: true,
    album_books: false,
    categories: false,
    attributes: false,
    optionSets: false,
    variant_templates: false,
    addon_templates: false,
    subcategories: false,
    import: false,
    import_media: false,
    prices: false,
    promotions: false,
    settings: false,
    staff: false,
    profile: true,
  },
  content_editor: {
    overview: false,
    announcements: true,
    notes: false,
    marketplace: false,
    publisher: true,
    export: true,
    albums: true,
    visiting_card: true,
    stock_alerts: false,
    crm: false,
    orders: false,
    products: true,
    galleries: true,
    album_reviews: true,
    album_published_reviews: true,
    album_requests: true,
    album_books: true,
    categories: true,
    attributes: true,
    optionSets: true,
    variant_templates: true,
    addon_templates: true,
    subcategories: true,
    import: true,
    import_media: true,
    prices: true,
    promotions: true,
    stock: false,
    catalog: true,
    settings: false,
    staff: false,
    profile: true,
  },
};

export function resolveEffectiveModules(
  role: string | undefined,
  permissionsOverride: Record<string, boolean> | null | undefined,
): Record<TenantModuleKey, boolean> {
  const baseRole = (role as TenantStaffRole) || 'content_editor';
  const base = { ...(ROLE_MODULES[baseRole] || ROLE_MODULES.content_editor) };
  if (permissionsOverride && typeof permissionsOverride === 'object') {
    for (const [key, value] of Object.entries(permissionsOverride)) {
      if (TENANT_MODULES.includes(key as TenantModuleKey) && typeof value === 'boolean') {
        base[key as TenantModuleKey] = value;
      }
    }
  }
  return base;
}

export function getSessionModules(session: AuthSession | null): Record<TenantModuleKey, boolean> | null {
  if (!session || session.type !== 'tenant_user') return null;
  const resolved = resolveEffectiveModules(session.role, session.permissions);
  if (!session.effectiveModules) return resolved;
  // Merge so new module keys appear without forcing re-login after deploy.
  return { ...resolved, ...session.effectiveModules };
}

/** First staff cabinet route for tenant_user after login (overview, products, …). */
export function staffHomePath(session: AuthSession | null): string {
  if (session?.type !== 'tenant_user') {
    return '/app/catalog';
  }
  const modules = getSessionModules(session);
  return modules ? defaultStaffPath(modules) : '/app/overview';
}

export function canAccessModule(
  session: AuthSession | null,
  moduleKey: TenantModuleKey,
): boolean {
  const modules = getSessionModules(session);
  return modules?.[moduleKey] === true;
}

export function isOwnerSession(session: AuthSession | null): boolean {
  // Platform grant = owner-level admin access; password change stays blocked in API/UI.
  return session?.type === 'tenant_user' && (session.role === 'owner' || session.platformGrant === true);
}

export function routeIdToModule(routeId: string): TenantModuleKey {
  if (routeId === 'stock-alerts') return 'stock_alerts';
  if (routeId === 'visiting-card') return 'visiting_card';
  if (routeId === 'variant-templates') return 'variant_templates';
  if (routeId === 'addon-templates') return 'addon_templates';
  if (routeId === 'option-sets') return 'optionSets';
  if (routeId === 'import-media') return 'import_media';
  return routeId as TenantModuleKey;
}

export function moduleToRouteId(moduleKey: TenantModuleKey): string {
  if (moduleKey === 'stock_alerts') return 'stock-alerts';
  if (moduleKey === 'visiting_card') return 'visiting-card';
  if (moduleKey === 'variant_templates') return 'variant-templates';
  if (moduleKey === 'addon_templates') return 'addon-templates';
  if (moduleKey === 'optionSets') return 'option-sets';
  if (moduleKey === 'import_media') return 'import-media';
  return moduleKey;
}

export function defaultStaffPath(modules: Record<TenantModuleKey, boolean>): string {
  // Do not prefer `catalog` — in website mode /app/catalog redirects to public `/`.
  const priority: TenantModuleKey[] = [
    'overview',
    'products',
    'orders',
    'crm',
    'announcements',
    'settings',
  ];
  for (const key of priority) {
    if (modules[key]) {
      return moduleToAppPath(key);
    }
  }
  return '/app/overview';
}

export function moduleToAppPath(moduleKey: TenantModuleKey): string {
  const paths: Record<TenantModuleKey, string> = {
    overview: '/app/overview',
    announcements: '/app/announcements',
    notes: '/app/notes',
    marketplace: '/app/marketplace',
    publisher: '/app/publisher',
    export: '/app/export',
    albums: '/app/albums',
    visiting_card: '/app/visiting-card',
    stock_alerts: '/app/stock-alerts',
    crm: '/app/crm',
    orders: '/app/orders',
    products: '/app/products',
    galleries: '/app/galleries',
    album_reviews: '/app/reviews',
    album_published_reviews: '/app/published-reviews',
    album_requests: '/app/requests',
    album_books: '/app/books',
    categories: '/app/categories',
    attributes: '/app/attributes',
    optionSets: '/app/option-sets',
    variant_templates: '/app/variant-templates',
    addon_templates: '/app/addon-templates',
    subcategories: '/app/categories',
    import: '/app/import',
    import_media: '/app/import-media',
    prices: '/app/prices',
    promotions: '/app/promotions',
    stock: '/app/stock',
    catalog: '/app/catalog',
    settings: '/app/settings',
    staff: '/app/staff',
    profile: '/app/profile',
  };
  return paths[moduleKey];
}

/** Module keys assignable as permission overrides (non-owner roles). */
export function overridableModulesForRole(role: TenantStaffRole): TenantModuleKey[] {
  const base = ROLE_MODULES[role];
  return TENANT_MODULES.filter((key) => key !== 'staff' && key !== 'settings');
}

export function roleLabelKey(role: TenantStaffRole | string): string {
  if (role === 'owner') return 'staff.roleOwner';
  if (role === 'manager') return 'staff.roleManager';
  if (role === 'content_editor') return 'staff.roleContentEditor';
  return 'staff.roleUnknown';
}

export function roleDescriptionKey(role: TenantStaffRole | string): string {
  if (role === 'owner') return 'staff.roleOwnerDesc';
  if (role === 'manager') return 'staff.roleManagerDesc';
  if (role === 'content_editor') return 'staff.roleContentEditorDesc';
  return 'staff.roleUnknownDesc';
}

export function roleCanDoKey(role: TenantStaffRole | string): string {
  if (role === 'owner') return 'staff.roleOwnerCan';
  if (role === 'manager') return 'staff.roleManagerCan';
  if (role === 'content_editor') return 'staff.roleContentEditorCan';
  return 'staff.roleUnknownCan';
}

export function roleCannotDoKey(role: TenantStaffRole | string): string {
  if (role === 'owner') return 'staff.roleOwnerCannot';
  if (role === 'manager') return 'staff.roleManagerCannot';
  if (role === 'content_editor') return 'staff.roleContentEditorCannot';
  return 'staff.roleUnknownCannot';
}
